import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import ExpenseCard from "../../components/expenses/ExpenseCard";
import StatCard from "../../components/dashboard/StatCard";
import StickyHeading from "../../components/common/StickyHeading";
import {
  Plus,
  Send,
  CalendarDays,
  Wallet,
  CircleDollarSign,
  Sparkles,
  Inbox,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { STATUSES, formatINR } from "../../lib/expenseOptions";
import { monthKeyIST } from "../../lib/datetime";
import { count, num, plural } from "../../lib/vocabulary";

const ALL = "__all__";

export default function MyExpensesPage({ soId = null }) {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [type, setType] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [submittingAll, setSubmittingAll] = useState(false);
  // From the backend's own list, never a frontend-owned array — see
  // AllExpensesPage.jsx for why a hardcoded copy of this is what caused
  // every expense submission to fail "Unknown expense_type". Fetched once,
  // independent of the filter-driven `fetchAll` below.
  const [expenseTypes, setExpenseTypes] = useState([]);

  async function fetchAll() {
    const params = {};
    if (type !== ALL) params.expense_type = type;
    if (status !== ALL) params.status = status;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    setLoading(true);
    try {
      // soId is set when a manager views their own claims via the My/Team
      // toggle — their unscoped list would otherwise include the whole team.
      const scoped = soId ? { ...params, so_id: soId } : params;
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/expenses", { params: scoped }),
        api.get("/expenses/summary", { params: soId ? { so_id: soId } : {} }),
      ]);
      setExpenses(list);
      setSummary(sum);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status, from, to]);

  useEffect(() => {
    api.get("/expenses/meta")
      .then(({ data }) => setExpenseTypes(data.expense_types || []))
      .catch(() => {}); // the filter just shows no options; the list still loads
  }, []);

  async function submitAll() {
    setSubmittingAll(true);
    try {
      const { data } = await api.put("/expenses/submit-all");
      toast.success(
        data.submitted_count
          ? `${plural(data.submitted_count, "draft")} submitted`
          : "No drafts to submit"
      );
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmittingAll(false);
    }
  }

  const draftCount = expenses.filter((e) => e.status === "Draft").length;
  // A SUMMARY THAT NEVER ARRIVED IS NOT A TOTAL OF ZERO (Pattern A / RC-11).
  // `(s.Approved?.amount || 0) + ...` collapsed "no expenses this month" and
  // "the summary call failed" into the same confident "₹0" — and the second
  // is the one where an officer with claims outstanding is told there are
  // none. `by_status` present but a status key absent IS a real zero: the
  // server only emits the statuses that have rows.
  const totals = useMemo(() => {
    const s = summary?.by_status;
    if (!s) return { month: null, pending: null, approved: null };
    return {
      month:
        (s.Approved?.amount || 0) +
        (s.Submitted?.amount || 0) +
        (s.Rejected?.amount || 0) +
        (s.Draft?.amount || 0),
      pending: s.Submitted?.amount || 0,
      approved: s.Approved?.amount || 0,
    };
  }, [summary]);
  const money = (v) => num(loading, v === null || v === undefined ? null : formatINR(v));

  const groups = useMemo(() => {
    const map = {};
    for (const e of expenses) {
      const k = monthKeyIST(e.expense_date);
      (map[k] ||= []).push(e);
    }
    return map;
  }, [expenses]);

  const hasFilters = type !== ALL || status !== ALL || !!from || !!to;
  const clear = () => {
    setType(ALL);
    setStatus(ALL);
    setFrom("");
    setTo("");
  };

  return (
    <div className="space-y-6" data-testid="my-expenses-page">
      <StickyHeading
        icon={Sparkles}
        label="My Expenses"
        title="My expenses"
        subtitle="Track, submit, and review your field expenses."
        testId="my-expenses-heading"
        actions={
          <div className="flex items-center gap-2">
            {draftCount > 0 && (
              <Button
                onClick={submitAll}
                disabled={submittingAll}
                variant="outline"
                className="h-11 border-callisto"
                data-testid="my-expenses-submit-all"
              >
                <Send className="h-4 w-4 mr-1.5" />
                Submit all {plural(draftCount, "draft")}
              </Button>
            )}
            <Link
              to="/expenses/new"
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-callisto-navy text-white h-11 px-5 text-sm font-semibold hover:bg-callisto-navy/90"
              data-testid="expense-new-cta"
            >
              <Plus className="h-4 w-4" />
              Log expense
            </Link>
          </div>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          testId="my-expenses-stat-month"
          label="This month total"
          value={money(totals.month)}
          helper="All entries"
          Icon={CalendarDays}
          loading={loading}
        />
        <StatCard
          testId="my-expenses-stat-pending"
          label="Pending approval"
          value={money(totals.pending)}
          helper={`${count(summary ? summary.by_status?.Submitted?.count || 0 : null)} submitted`}
          Icon={Wallet}
          loading={loading}
        />
        <StatCard
          testId="my-expenses-stat-approved"
          label="Approved"
          value={money(totals.approved)}
          helper={`${count(summary ? summary.by_status?.Approved?.count || 0 : null)} entries`}
          Icon={CircleDollarSign}
          accent
          loading={loading}
        />
      </div>

      {/* Filters */}
      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="my-expenses-filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {expenseTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="my-expenses-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={to || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="my-expenses-filter-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="my-expenses-filter-to"
          />
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            onClick={clear}
            className="mt-2 h-9 text-callisto-muted hover:text-callisto-navy"
            data-testid="my-expenses-filters-clear"
          >
            <X className="h-3 w-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* Expense list */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : expenses.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-6" data-testid="my-expenses-list">
          {Object.entries(groups).map(([k, items]) => (
            <div key={k}>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
                {k} · {items.length}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {items.map((ex) => (
                  <ExpenseCard key={ex.id} expense={ex} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
      <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
        <Inbox className="h-6 w-6 text-callisto-navy" />
      </div>
      <p className="text-sm font-semibold text-callisto-navy">No expenses yet</p>
      <p className="mt-1 text-xs text-callisto-muted max-w-sm">
        Log your first expense and it will show up here with its current status.
      </p>
      <Link
        to="/expenses/new"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-callisto-navy text-white px-4 py-2.5 text-sm font-semibold hover:bg-callisto-navy/90"
      >
        <Plus className="h-4 w-4" />
        Log expense
      </Link>
    </div>
  );
}
